import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Users, BookOpen, Image, FileText, User, ChevronRight, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { API_URL } from '../config/api';

const API = API_URL;

// Helper to resolve image URLs
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const BACKEND_URL = '';  // Use relative URLs
  return url.startsWith('/') ? `${BACKEND_URL}${url}` : `${BACKEND_URL}/${url}`;
};

const SearchResults = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';
  
  const [searchInput, setSearchInput] = useState(query);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({
    creators: [],
    series: [],
    novels: [],
    artworks: []
  });

  useEffect(() => {
    setSearchInput(query);
    if (query.trim().length >= 2) {
      performSearch(query);
    } else {
      setResults({ creators: [], series: [], novels: [], artworks: [] });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const performSearch = async (searchQuery) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/search`, {
        params: { q: searchQuery, limit: 10 }
      });
      setResults(response.data);
    } catch (error) {
      console.error('Search error:', error);
      setResults({ creators: [], series: [], novels: [], artworks: [] });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchInput.trim().length >= 2) {
      setSearchParams({ q: searchInput.trim() });
    }
  };

  const totalResults = 
    results.creators.length + 
    results.series.length + 
    results.novels.length + 
    results.artworks.length;

  return (
    <div className="min-h-screen bg-black pt-16 pb-20" data-testid="search-results-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Search Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-4">Search</h1>
          
          <form onSubmit={handleSearchSubmit} className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search creators, comics, novels, artworks..."
                className="pl-10 bg-gray-900 border-gray-700 text-white h-12 text-lg"
                data-testid="search-input"
              />
            </div>
            <Button 
              type="submit" 
              className="bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 h-12 px-6"
              data-testid="search-submit"
            >
              Search
            </Button>
          </form>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            <span className="ml-3 text-gray-400">Searching...</span>
          </div>
        )}

        {/* Results */}
        {!loading && query && (
          <div className="space-y-8">
            {/* Results Summary */}
            <div className="text-gray-400">
              {totalResults > 0 ? (
                <span>Found {totalResults} result{totalResults !== 1 ? 's' : ''} for &quot;{query}&quot;</span>
              ) : (
                <span>No results found for &quot;{query}&quot;</span>
              )}
            </div>

            {/* Creators Section */}
            {results.creators.length > 0 && (
              <section data-testid="creators-section">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                  <Users className="w-5 h-5 mr-2 text-purple-400" />
                  Creators ({results.creators.length})
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {results.creators.map((creator) => (
                    <Link
                      key={creator.user_id || creator.handle}
                      to={`/u/${creator.handle}`}
                      className="block"
                      data-testid={`creator-result-${creator.handle}`}
                    >
                      <Card className="bg-gray-900 border-gray-800 hover:border-purple-500 transition-colors h-full">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-full bg-purple-500/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                              {creator.avatar_url ? (
                                <img 
                                  src={resolveImageUrl(creator.avatar_url)} 
                                  alt={creator.display_name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <User className="w-7 h-7 text-purple-400" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-white truncate">{creator.display_name}</h3>
                                {creator.verified && (
                                  <Badge className="bg-blue-500/20 text-blue-400 text-xs">Verified</Badge>
                                )}
                              </div>
                              <p className="text-sm text-gray-400">@{creator.handle}</p>
                              {creator.bio && (
                                <p className="text-xs text-gray-500 mt-1 line-clamp-1">{creator.bio}</p>
                              )}
                            </div>
                            <ChevronRight className="w-5 h-5 text-gray-600 flex-shrink-0" />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* Series Section */}
            {results.series.length > 0 && (
              <section data-testid="series-section">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                  <BookOpen className="w-5 h-5 mr-2 text-orange-400" />
                  Comics ({results.series.length})
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {results.series.map((series) => (
                    <Link
                      key={series.series_id}
                      to={`/series/${series.series_id}`}
                      className="block"
                      data-testid={`series-result-${series.series_id}`}
                    >
                      <Card className="bg-gray-900 border-gray-800 hover:border-orange-500 transition-colors overflow-hidden group">
                        <div className="aspect-[3/4] bg-gray-800 relative">
                          {series.cover_url ? (
                            <img 
                              src={resolveImageUrl(series.cover_url)} 
                              alt={series.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <BookOpen className="w-10 h-10 text-gray-600" />
                            </div>
                          )}
                        </div>
                        <CardContent className="p-3">
                          <h3 className="font-semibold text-white text-sm truncate">{series.title}</h3>
                          {series.creator_name && (
                            <p className="text-xs text-gray-400 truncate">by {series.creator_name}</p>
                          )}
                          {series.genres?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {series.genres.slice(0, 2).map(g => (
                                <Badge key={g} variant="secondary" className="text-xs bg-gray-800 text-gray-400">{g}</Badge>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* Novels Section */}
            {results.novels.length > 0 && (
              <section data-testid="novels-section">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-green-400" />
                  Novels ({results.novels.length})
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {results.novels.map((novel) => (
                    <Link
                      key={novel.novel_id}
                      to={`/novel/${novel.novel_id}`}
                      className="block"
                      data-testid={`novel-result-${novel.novel_id}`}
                    >
                      <Card className="bg-gray-900 border-gray-800 hover:border-green-500 transition-colors overflow-hidden group">
                        <div className="aspect-[3/4] bg-gray-800 relative">
                          {novel.cover_url ? (
                            <img 
                              src={resolveImageUrl(novel.cover_url)} 
                              alt={novel.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <FileText className="w-10 h-10 text-gray-600" />
                            </div>
                          )}
                        </div>
                        <CardContent className="p-3">
                          <h3 className="font-semibold text-white text-sm truncate">{novel.title}</h3>
                          {novel.creator_name && (
                            <p className="text-xs text-gray-400 truncate">by {novel.creator_name}</p>
                          )}
                          {novel.genres?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {novel.genres.slice(0, 2).map(g => (
                                <Badge key={g} variant="secondary" className="text-xs bg-gray-800 text-gray-400">{g}</Badge>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* Artworks Section */}
            {results.artworks.length > 0 && (
              <section data-testid="artworks-section">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                  <Image className="w-5 h-5 mr-2 text-pink-400" />
                  Artworks ({results.artworks.length})
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {results.artworks.map((artwork) => (
                    <Link
                      key={artwork.artwork_id}
                      to={`/art/${artwork.artwork_id}`}
                      className="block"
                      data-testid={`artwork-result-${artwork.artwork_id}`}
                    >
                      <Card className="bg-gray-900 border-gray-800 hover:border-pink-500 transition-colors overflow-hidden group">
                        <div className="aspect-square bg-gray-800 relative">
                          {artwork.image_url ? (
                            <img 
                              src={resolveImageUrl(artwork.image_url)} 
                              alt={artwork.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Image className="w-10 h-10 text-gray-600" />
                            </div>
                          )}
                        </div>
                        <CardContent className="p-3">
                          <h3 className="font-semibold text-white text-sm truncate">{artwork.title}</h3>
                          {artwork.creator_name && (
                            <p className="text-xs text-gray-400 truncate">by {artwork.creator_name}</p>
                          )}
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* No Results */}
            {totalResults === 0 && (
              <div className="text-center py-16" data-testid="no-results">
                <Search className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-white mb-2">No results found</h2>
                <p className="text-gray-400 mb-6">
                  We couldn&apos;t find anything matching &quot;{query}&quot;
                </p>
                <div className="text-sm text-gray-500">
                  <p>Try:</p>
                  <ul className="list-disc list-inside mt-2">
                    <li>Checking your spelling</li>
                    <li>Using different keywords</li>
                    <li>Searching for creator handles (e.g., @bubbles)</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Initial State (no query) */}
        {!loading && !query && (
          <div className="text-center py-16">
            <Search className="w-16 h-16 text-gray-700 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Start searching</h2>
            <p className="text-gray-400">
              Search for creators, comics, novels, and artworks
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResults;
